// WebM → MP3 変換サービス。
// - デコード（decodeAudioData）は AudioContext が Worker で使えないためメインスレッドで行い、
//   変換ごとに生成した AudioContext は必ず close する
// - lamejs によるエンコードは常駐 Worker で行う（変換のたびに Worker を作らない）
// - PCM データは Transferable として渡し、コピーを避ける

interface PendingConversion {
	resolve: (blob: Blob) => void;
	reject: (error: Error) => void;
}

let worker: Worker | null = null;
let nextRequestId = 0;
const pending = new Map<number, PendingConversion>();

const getWorker = (): Worker => {
	if (!worker) {
		worker = new Worker(new URL("../workers/mp3-encoder.worker.ts", import.meta.url), { type: "module" });

		worker.onmessage = (e: MessageEvent) => {
			const { id, type, data } = e.data as { id: number; type: string; data: unknown };
			const entry = pending.get(id);
			if (!entry) return;

			if (type === "encoded") {
				pending.delete(id);
				entry.resolve(data as Blob);
			} else if (type === "error") {
				pending.delete(id);
				entry.reject(new Error(String(data)));
			}
		};

		worker.onerror = (err) => {
			console.error("MP3 Worker エラー:", err);
			for (const entry of pending.values()) {
				entry.reject(new Error("MP3 Worker error"));
			}
			pending.clear();
		};
	}
	return worker;
};

/** WebM形式のBlobをMP3に変換する */
export async function convertWebmToMp3(webmBlob: Blob): Promise<Blob> {
	// デコードはメインスレッドで行う（Worker では AudioContext が使えない）
	const audioContext = new AudioContext();
	let pcm: Float32Array;
	let sampleRate: number;
	try {
		const audioBuffer = await audioContext.decodeAudioData(await webmBlob.arrayBuffer());
		// QUIRK: モノラルへダウンミックス（チャンネル0のみ使用）。音声用途のサイズ削減で意図的
		// AudioBuffer の内部バッファは Transferable にできないブラウザがあるため、
		// 新しい Float32Array へコピーしてから移譲する
		pcm = new Float32Array(audioBuffer.length);
		audioBuffer.copyFromChannel(pcm, 0);
		sampleRate = audioBuffer.sampleRate;
	} finally {
		audioContext.close().catch(() => {});
	}

	const buffer = pcm.buffer;

	const id = nextRequestId++;
	const encoderWorker = getWorker();

	return new Promise<Blob>((resolve, reject) => {
		pending.set(id, { resolve, reject });
		// PCM バッファは Transferable として渡す（構造化クローンによる全コピーを避ける）
		encoderWorker.postMessage({ id, audioData: buffer, sampleRate }, [buffer]);
	});
}
